import { object, string, ref } from 'yup'
import { differenceInYears } from 'date-fns'
import emojiRegex from 'emoji-regex'
import { PASSWORD_REGEX } from '~/utils/constants'

// "YYYY-MM-DD" built from the 3 date selects: one left empty gives a broken
// string, reported as missing (it used to fail the age check and show the
// "13 years old minimum" message)
const BIRTHDAY_FORMAT = /^\d{4}-\d{2}-\d{2}$/

// a nickname is public (profile URL, posts, rankings): never an e-mail address, as the API checks too
// (User::REGEX_EMAIL_NICKNAME)
const EMAIL_NICKNAME = /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i
const nicknameSchema = string()
  .required('Votre pseudo est requis')
  .test(
    'not-an-email',
    'Votre pseudo ne doit pas être une adresse e-mail.',
    (value) => !EMAIL_NICKNAME.test((value ?? '').trim()),
  )

const isRealDate = (value: string) => {
  const [year, month, day] = value.split('-').map(Number)
  const date = new Date(year, month - 1, day)

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  )
}

// one message at a time: each test passes when an earlier one already failed
const birthdaySchema = (ageMessage: string) =>
  string()
    .test(
      'birthday-required',
      'Votre date de naissance est requise.',
      (value) => BIRTHDAY_FORMAT.test(value ?? ''),
    )
    .test(
      'birthday-exists',
      "Cette date de naissance n'existe pas.",
      (value) => !BIRTHDAY_FORMAT.test(value ?? '') || isRealDate(value!),
    )
    .test(
      'birthday-check',
      ageMessage,
      (value) =>
        !BIRTHDAY_FORMAT.test(value ?? '') ||
        !isRealDate(value!) ||
        differenceInYears(new Date(), new Date(value!)) >= 13,
    )

export const registrationFormSchema = object().shape({
  civility: string().required('Votre civilité est requise.'),
  lastname: string().required('Votre nom est requis.'),
  firstname: string().required('Votre prénom est requis.'),
  email: string()
    .required('Votre adresse e-mail est requise.')
    .email('Votre adresse e-mail est incorrecte.'),
  password: string()
    .required('Votre mot de passe est requis.')
    .matches(
      PASSWORD_REGEX,
      'Caractères spéciaux autorisés : _@$€!={}()%#^+*?&-./\\<>',
    ),
  password_confirmation: string().equals(
    [ref('password')],
    'Les mots de passe doivent correspondre',
  ),
  nickname: nicknameSchema,
  birthday: birthdaySchema(
    'Désolé, vous devez avoir 13 ans minimum pour pouvoir vous inscrire.',
  ),
})

export const updateProfileFormSchema = object().shape({
  civility: string().required('Votre civilité est requise.'),
  firstname: string().required('Votre prénom est requis.'),
  lastname: string().required('Votre nom est requis.'),
  nickname: nicknameSchema,
  birthday: birthdaySchema('Désolé, vous devez avoir 13 ans minimum.'),
})

export const passwordUpdateFormSchema = object().shape({
  current_password: string().required('Votre ancien mot de passe est requis'),
  password: string()
    .required('Votre nouveau mot de passe est requis.')
    .matches(
      PASSWORD_REGEX,
      'Caractères spéciaux autorisés : _@$€!={}()%#^+*?&-./\\<>',
    ),
  password_confirmation: string().oneOf(
    [ref('password')],
    'Les mots de passe doivent correspondre',
  ),
})

// A Google sign-up never chose its password: the current one is not asked
export const passwordCreateFormSchema = passwordUpdateFormSchema.omit([
  'current_password',
])

const regex = emojiRegex()

export const passwordResetFormSchema = object().shape({
  password: string()
    .required('Votre mot de passe est requis.')
    .matches(
      PASSWORD_REGEX,
      'Caractères spéciaux autorisés : _@$€!={}()%#^+*?&-./\\<>',
    ),
  password_confirmation: string().oneOf(
    [ref('password')],
    'Les mots de passe doivent correspondre',
  ),
})

export const reviewSchema = string()
  .min(
    140,
    (msg) =>
      `Il manque ${msg.min - msg.value.length} caractère${
        msg.min - msg.value.length > 1 ? 's' : ''
      }`,
  )
  .test('emoji', 'Les emojis ne sont pas autorisés', (value) => {
    return value?.match(regex) === null
  })

export const titleSchema = string()
  .max(50, 'Le titre de votre avis doit faire 50 caractères maximum')
  .test('emoji', 'Les emojis ne sont pas autorisés', (value) => {
    return value?.match(regex) === null
  })
